import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Milk, Plus } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import { useAuth } from '../../context/AuthContext';
import {
  PACK_HAS_SUBSCRIBERS_MSG,
  PACK_KINDS,
  createPack,
  listPacks,
  packErrorMessage,
  packHasActiveSubscriptions,
  updatePack,
} from '../../api/subscriptions';
import PackCard from './PackCard';
import PackFormDrawer from './PackFormDrawer';
import styles from './Subscriptions.module.css';

const KIND_TABS = [
  { key: '', label: 'All' },
  ...PACK_KINDS.map((k) => ({ key: k.value, label: k.label })),
];

export default function Packs() {
  const { user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [kindTab, setKindTab] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [togglingId, setTogglingId] = useState(null);

  const loadPacks = useCallback(async () => {
    setLoading(true);
    setError('');
    setActionError('');
    try {
      const list = await listPacks(user.idToken, { kind: kindTab || undefined });
      setPacks(list);
    } catch (err) {
      setError(err.message || 'Could not load subscription packs.');
    } finally {
      setLoading(false);
    }
  }, [user.idToken, kindTab]);

  useEffect(() => {
    loadPacks();
  }, [loadPacks]);

  const openAdd = () => {
    setEditing(null);
    setSaveError('');
    setDrawerOpen(true);
  };

  const openEdit = (pack) => {
    setEditing(pack);
    setSaveError('');
    setDrawerOpen(true);
  };

  const handleSave = async (data) => {
    setSaving(true);
    setSaveError('');
    try {
      if (editing) {
        if (
          data.status === 'INACTIVE'
          && editing.status === 'ACTIVE'
          && packHasActiveSubscriptions(editing)
        ) {
          setSaveError(PACK_HAS_SUBSCRIBERS_MSG);
          return;
        }
        await updatePack(editing.id, data, user.idToken);
        // Kind may have changed — show All so the pack doesn't vanish from Milk/Water tab.
        if (data.kind && data.kind !== editing.kind && kindTab && kindTab !== data.kind) {
          setKindTab('');
        }
      } else {
        await createPack(data, user.idToken);
      }
      setDrawerOpen(false);
      setEditing(null);
      await loadPacks();
    } catch (err) {
      const detail = err.detail || err.body?.message;
      setSaveError(
        packErrorMessage(err)
        || (detail ? `${err.message}: ${detail}` : null)
        || 'Could not save this pack.',
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (pack) => {
    const nextStatus = pack.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    if (nextStatus === 'INACTIVE' && packHasActiveSubscriptions(pack)) {
      setActionError(PACK_HAS_SUBSCRIBERS_MSG);
      return;
    }
    setTogglingId(pack.id);
    setActionError('');
    try {
      const updated = await updatePack(pack.id, { status: nextStatus }, user.idToken);
      setPacks((prev) => prev.map((p) => (p.id === pack.id ? { ...p, ...updated } : p)));
    } catch (err) {
      setActionError(packErrorMessage(err));
    } finally {
      setTogglingId(null);
    }
  };

  const filtered = useMemo(() => packs, [packs]);

  return (
    <div>
      <PageHeader
        eyebrow="Subscriptions"
        title="Packs"
        subtitle="Create milk, water, and curd packs customers can subscribe to. Fee mode and discount are charged by the server."
        actions={(
          <Button variant="primary" icon={Plus} onClick={openAdd}>
            New pack
          </Button>
        )}
      />

      <div className={styles.filterRow}>
        <div className={styles.filterTabs}>
          {KIND_TABS.map((tab) => (
            <button
              key={tab.key || 'all'}
              type="button"
              className={`${styles.filterTab} ${kindTab === tab.key ? styles.filterTabActive : ''}`}
              onClick={() => setKindTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className={styles.hint}>Loading packs…</p>}

      {!loading && error && (
        <div className={styles.hint}>
          <p className={styles.errorText}>
            <AlertTriangle size={12} /> {error}
          </p>
          <Button type="button" variant="outline" size="sm" onClick={loadPacks} style={{ marginTop: 8 }}>
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && actionError && (
        <p className={styles.errorText} style={{ marginBottom: 12 }}>
          <AlertTriangle size={12} /> {actionError}
        </p>
      )}

      {!loading && !error && filtered.length === 0 && (
        <EmptyState
          icon={Milk}
          title="No packs yet"
          description="Create a pack with unit price, delivery fee mode, and default weekdays."
          action={<Button variant="primary" icon={Plus} onClick={openAdd}>New pack</Button>}
        />
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className={styles.packGrid}>
          {filtered.map((pack) => (
            <PackCard
              key={pack.id}
              pack={pack}
              onEdit={openEdit}
              onToggle={toggleStatus}
              toggling={togglingId === pack.id}
            />
          ))}
        </div>
      )}

      <PackFormDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditing(null); }}
        onSave={handleSave}
        pack={editing}
        saving={saving}
        error={saveError}
      />
    </div>
  );
}
